/**
 * BrainDumpOverlay Component
 *
 * Full-screen overlay that handles the 3-phase brain dump flow:
 *   1. Recording  — big pulsing mic, timer, stop button
 *   2. Processing — spinner while Whisper + Claude parse the audio
 *   3. Review     — editable list of extracted tasks, "Add All" / per-task toggle
 *
 * On completion, calls onTasksCreated() so the caller can refetch.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Mic, Square, Loader2, X, Sparkles, Check, Trash2, AlertCircle, Plus } from 'lucide-react';
import clsx from 'clsx';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { submitBrainDump, batchCreateTasks } from '../api/brainDump';
import type { BrainDumpTask } from '../api/brainDump';

type Phase = 'recording' | 'processing' | 'review' | 'success';

interface ReviewTask extends BrainDumpTask {
  selected: boolean;
}

interface BrainDumpOverlayProps {
  onClose: () => void;
  onTasksCreated?: () => void;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function priorityLabel(p: number): string {
  return ['', 'P1', 'P2', 'P3', 'P4'][p] ?? 'P3';
}

function priorityColor(p: number): string {
  return (
    [
      '',
      'text-red-500 dark:text-red-400',
      'text-orange-500 dark:text-orange-400',
      'text-text-secondary-light dark:text-text-secondary-dark',
      'text-text-tertiary-light dark:text-text-tertiary-dark',
    ][p] ?? ''
  );
}

export function BrainDumpOverlay({ onClose, onTasksCreated }: BrainDumpOverlayProps) {
  const recording = useVoiceRecording({ maxDuration: 120 });

  const [phase, setPhase] = useState<Phase>('recording');
  const [reviewTasks, setReviewTasks] = useState<ReviewTask[]>([]);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  // Use a ref so the auto-start effect closure is always current
  const recordingRef = useRef(recording);
  recordingRef.current = recording;

  // Auto-start recording once on mount — useEffect with [] avoids the
  // ref-callback re-fire loop that was caused by hasStarted state changes.
  useEffect(() => {
    recordingRef.current.startRecording().catch(() => {
      setError('Could not access microphone. Check permissions.');
    });
     
  }, []); // intentionally empty — run once on mount only

  const startRecordingManually = useCallback(async () => {
    setError(null);
    try {
      await recording.startRecording();
    } catch {
      setError('Could not access microphone. Check permissions.');
    }
  }, [recording]);

  const handleStop = async () => {
    if (!recording.isRecording) return;
    setPhase('processing');
    setError(null);

    try {
      const audioBlob = await recording.stopRecording();
      const result = await submitBrainDump(audioBlob);
      setTranscript(result.transcript);
      setReviewTasks(result.tasks.map((t) => ({ ...t, selected: true })));
      setPhase('review');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Processing failed';
      setError(msg);
      setPhase('recording');
      // Do NOT auto-restart here — user sees the error and can tap to try again
    }
  };

  const handleToggleTask = (index: number) => {
    setReviewTasks((prev) =>
      prev.map((t, i) => (i === index ? { ...t, selected: !t.selected } : t)),
    );
  };

  const handleEditContent = (index: number, value: string) => {
    setReviewTasks((prev) => prev.map((t, i) => (i === index ? { ...t, content: value } : t)));
  };

  const handleRemoveTask = (index: number) => {
    setReviewTasks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddBlankTask = () => {
    setReviewTasks((prev) => [
      ...prev,
      {
        content: '',
        dueDate: null,
        priority: 3,
        projectId: null,
        project: null,
        selected: true,
      },
    ]);
  };

  const handleAddAll = async () => {
    const selected = reviewTasks.filter((t) => t.selected && t.content.trim());
    if (selected.length === 0) return;

    setIsSaving(true);
    setError(null);
    try {
      const result = await batchCreateTasks(selected);
      setSavedCount(result.created);
      setPhase('success');
      onTasksCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save tasks');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedCount = reviewTasks.filter((t) => t.selected).length;

  // Scale the outer ping ring based on live audio level (1× quiet → 1.5× loud)
  const ringScale = 1 + recording.audioLevel * 0.5;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-surface-base-light dark:bg-surface-base-dark">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-safe pb-3 border-b border-border-subtle-light dark:border-border-subtle-dark">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-500 dark:text-primary-400" />
          <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">
            Brain Dump
          </span>
          {/* Phase indicator — gives users orientation across the multi-step flow */}
          <span className="hidden sm:inline text-xs text-text-tertiary-light dark:text-text-tertiary-dark ml-2">
            {phase === 'recording' && '· Step 1 of 3 — record'}
            {phase === 'processing' && '· Step 2 of 3 — processing'}
            {phase === 'review' && '· Step 3 of 3 — review'}
            {phase === 'success' && '· Done'}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-text-tertiary-light dark:text-text-tertiary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-y-auto">
        {/* ── Recording phase ── */}
        {phase === 'recording' && (
          <div className="flex flex-col items-center gap-6 py-12">
            {recording.isRecording ? (
              <>
                {/* Pulsing record indicator — outer ring scales with live audio level */}
                <div className="relative flex items-center justify-center">
                  <div
                    className="absolute w-32 h-32 rounded-full bg-red-500/20 animate-ping"
                    style={{ transform: `scale(${ringScale})` }}
                  />
                  <div className="absolute w-24 h-24 rounded-full bg-red-500/30 animate-pulse" />
                  <button
                    onClick={handleStop}
                    className="relative w-20 h-20 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center shadow-lg transition-colors"
                    aria-label="Stop recording"
                  >
                    <Square className="w-8 h-8 text-white fill-current" />
                  </button>
                </div>
                <div className="text-center space-y-1">
                  <p className="text-3xl font-mono font-bold text-text-primary-light dark:text-text-primary-dark tabular-nums">
                    {formatDuration(recording.duration)}
                  </p>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Tap to stop · max 2 min
                  </p>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={startRecordingManually}
                  className="w-20 h-20 rounded-full bg-primary-700 hover:bg-primary-800 flex items-center justify-center shadow-lg transition-colors"
                  aria-label="Start recording"
                >
                  <Mic className="w-8 h-8 text-white" />
                </button>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center">
                  Tap to start recording
                  <span className="block text-xs text-text-tertiary-light dark:text-text-tertiary-dark mt-0.5">
                    Up to 2 minutes
                  </span>
                </p>
              </>
            )}

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark text-center max-w-xs">
              Speak naturally — "I need to call the dentist Thursday, pick up groceries, review the Tyler PR..."
            </p>
          </div>
        )}

        {/* ── Processing phase ── */}
        {phase === 'processing' && (
          <div className="flex flex-col items-center gap-4 py-12">
            <div className="relative">
              <Loader2 className="w-12 h-12 text-primary-500 animate-spin" />
            </div>
            <div className="text-center space-y-1">
              <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                Thinking…
              </p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Transcribing and extracting tasks
              </p>
            </div>
          </div>
        )}

        {/* ── Review phase ── */}
        {phase === 'review' && (
          <div className="w-full max-w-lg py-4 space-y-3">
            {transcript && (
              <details className="group">
                <summary className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark cursor-pointer select-none hover:text-text-secondary-light dark:hover:text-text-secondary-dark">
                  Transcript ▾
                </summary>
                <p className="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark bg-surface-hover-light dark:bg-surface-hover-dark rounded p-2 leading-relaxed">
                  {transcript}
                </p>
              </details>
            )}

            <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
              {reviewTasks.length} task{reviewTasks.length !== 1 ? 's' : ''} found — review and add
            </p>

            <div className="space-y-2">
              {reviewTasks.map((task, i) => (
                <div
                  key={i}
                  className={clsx(
                    'flex items-start gap-3 p-3 rounded-xl border transition-colors',
                    task.selected
                      ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/20'
                      : 'border-border-subtle-light dark:border-border-subtle-dark bg-surface-hover-light dark:bg-surface-hover-dark opacity-50',
                  )}
                >
                  {/* Select toggle */}
                  <button
                    onClick={() => handleToggleTask(i)}
                    className={clsx(
                      'mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                      task.selected
                        ? 'bg-primary-500 border-primary-500'
                        : 'border-border-subtle-light dark:border-border-subtle-dark',
                    )}
                    aria-label={task.selected ? 'Deselect task' : 'Select task'}
                  >
                    {task.selected && <Check className="w-3 h-3 text-white" />}
                  </button>

                  {/* Content — editable, hover/focus give visible affordance
                      so users realize they can fix what the AI extracted */}
                  <div className="flex-1 min-w-0 space-y-1">
                    <input
                      type="text"
                      value={task.content}
                      onChange={(e) => handleEditContent(i, e.target.value)}
                      className="w-full bg-transparent text-sm text-text-primary-light dark:text-text-primary-dark rounded px-1 -mx-1 hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark focus:outline-none focus:ring-1 focus:ring-primary-500 transition-colors"
                      aria-label="Task content (editable)"
                      title="Click to edit"
                    />
                    <div className="flex items-center gap-2 text-xs">
                      <span className={priorityColor(task.priority)}>
                        {priorityLabel(task.priority)}
                      </span>
                      {task.dueDate && (
                        <span className="text-text-tertiary-light dark:text-text-tertiary-dark">
                          {task.dueDate}
                        </span>
                      )}
                      {task.project && (
                        <span className="text-text-tertiary-light dark:text-text-tertiary-dark truncate">
                          {task.project}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => handleRemoveTask(i)}
                    className="flex-shrink-0 p-1 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    aria-label="Remove task"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {reviewTasks.length === 0 && (
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark text-center py-4">
                No tasks remaining. Add one below or record again.
              </p>
            )}

            {/* Add task — recovers from "AI missed one" without re-recording */}
            <button
              type="button"
              onClick={handleAddBlankTask}
              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark border border-dashed border-border-subtle-light dark:border-border-subtle-dark rounded-xl hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add task
            </button>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-4 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        {/* ── Success phase ── */}
        {phase === 'success' && (
          <div className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                {savedCount} task{savedCount !== 1 ? 's' : ''} added!
              </p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                They're in your task list now.
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary-700 hover:bg-primary-800 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>

      {/* Footer — only during review */}
      {phase === 'review' && reviewTasks.length > 0 && (
        <div className="px-4 pb-safe pt-3 border-t border-border-subtle-light dark:border-border-subtle-dark space-y-2">
          <button
            onClick={handleAddAll}
            disabled={isSaving || selectedCount === 0}
            className="w-full px-4 py-3 bg-primary-700 hover:bg-primary-800 text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding…
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Add {selectedCount} task{selectedCount !== 1 ? 's' : ''}
              </>
            )}
          </button>
          <button
            onClick={() => {
              setPhase('recording');
              setReviewTasks([]);
            }}
            className="w-full px-4 py-2 text-sm text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark rounded-xl transition-colors"
          >
            Record again
          </button>
        </div>
      )}
    </div>
  );
}
